import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api';
import { useAuth, hasRole } from '@/lib/auth';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Search, Pause, Play, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const LEVELS = ['debug', 'info', 'warn', 'error', 'critical'];
const LEVEL_CLASS = {
  debug: 'text-slate-300 bg-slate-500/10 border-slate-500/20',
  info: 'text-sky-300 bg-sky-500/10 border-sky-500/20',
  warn: 'text-amber-300 bg-amber-500/10 border-amber-500/25',
  error: 'text-red-300 bg-red-500/10 border-red-500/25',
  critical: 'text-red-200 bg-red-500/15 border-red-500/30',
};

export default function LogsPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isAdmin = hasRole(user, ['admin']);
  const [logs, setLogs] = useState([]);
  const [services, setServices] = useState([]);
  const [level, setLevel] = useState('all');
  const [serviceId, setServiceId] = useState('all');
  const [q, setQ] = useState('');
  const [tail, setTail] = useState(true);
  const lastIdsRef = useRef(new Set());

  useEffect(() => {
    api.get('/services').then(({ data }) => setServices(data));
  }, []);

  const load = async () => {
    const params = { limit: 200 };
    if (level !== 'all') params.level = level;
    if (serviceId !== 'all') params.service_id = serviceId;
    if (q) params.q = q;
    const { data } = await api.get('/logs', { params });
    setLogs(data);
  };

  useEffect(() => {
    load();
    if (!tail) return;
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, [level, serviceId, q, tail]);

  const seedDemo = async () => {
    try { await api.post('/demo/seed-logs?count=30'); toast.success(t('success')); load(); }
    catch (e) { toast.error(t('error')); }
  };

  return (
    <div className="space-y-4" data-testid="logs-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold">{t('logs_title')}</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">{logs.length} entries</p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && <Button variant="outline" size="sm" onClick={seedDemo} data-testid="logs-seed-button"><Sparkles className="h-3.5 w-3.5 mr-1" />{t('logs_seed')}</Button>}
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-border bg-card">
            <Switch checked={tail} onCheckedChange={setTail} data-testid="logs-tail-toggle" />
            <span className="text-xs flex items-center gap-1">
              {tail ? <Play className="h-3 w-3 text-[hsl(var(--primary))]" /> : <Pause className="h-3 w-3" />} {t('logs_tail')}
            </span>
          </div>
        </div>
      </div>

      <Card className="p-3 bg-card border border-border flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="h-4 w-4 absolute left-3 top-2.5 text-[hsl(var(--muted-foreground))]" />
          <Input
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder={t('logs_search_placeholder')} className="pl-9"
            data-testid="logs-query-input"
          />
        </div>
        <Select value={level} onValueChange={setLevel}>
          <SelectTrigger className="w-[160px]" data-testid="logs-severity-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('logs_all_levels')}</SelectItem>
            {LEVELS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={serviceId} onValueChange={setServiceId}>
          <SelectTrigger className="w-[220px]" data-testid="logs-service-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('logs_all_services')}</SelectItem>
            {services.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </Card>

      <Card className="bg-card border border-border">
        <div className="max-h-[60vh] overflow-y-auto">
          <table className="w-full text-xs" data-testid="data-table-logs">
            <thead className="sticky top-0 bg-card z-10">
              <tr className="text-left text-[10px] uppercase tracking-wide text-[hsl(var(--muted-foreground))] border-b border-border">
                <th className="px-3 py-2">When</th><th className="px-3 py-2">Level</th><th className="px-3 py-2">Service</th><th className="px-3 py-2">Message</th>
              </tr>
            </thead>
            <tbody className="font-mono">
              {logs.map((l) => {
                const isNew = !lastIdsRef.current.has(l.id);
                if (isNew) lastIdsRef.current.add(l.id);
                return (
                  <tr key={l.id} className={`border-b border-border/40 hover:bg-accent/40 ${isNew && tail ? 'bomm-row-flash' : ''}`} data-testid={`logs-row-${l.id}`}>
                    <td className="px-3 py-2 text-[hsl(var(--muted-foreground))] whitespace-nowrap">{l.created_at ? new Date(l.created_at).toLocaleString() : ''}</td>
                    <td className="px-3 py-2"><span className={`px-1.5 py-0.5 rounded border text-[10px] uppercase ${LEVEL_CLASS[l.level] || ''}`}>{l.level}</span></td>
                    <td className="px-3 py-2">{l.service_name || '—'}</td>
                    <td className="px-3 py-2">{l.message}</td>
                  </tr>
                );
              })}
              {logs.length === 0 && <tr><td colSpan={4} className="px-4 py-8 text-center text-[hsl(var(--muted-foreground))] font-sans">{t('no_data')}</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
